import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { cn } from "@/lib/utils";
import { BUTTON_SIZE, BUTTON_VARIANT, buttonClassName } from "./buttonClassName";
import { WilsonFace } from "./WilsonFace";
export { buttonClassName };
const BUSY_LOOKS_IDLE: Record<keyof typeof BUTTON_VARIANT, string> = {
  primary: "disabled:bg-ink disabled:text-surface",
  secondary: "disabled:bg-surface disabled:text-ink disabled:shadow-hairline-strong",
  outline: "disabled:bg-surface disabled:text-ink disabled:shadow-hairline-strong",
  ghost: "disabled:bg-transparent disabled:text-ink-2",
  control: "disabled:bg-transparent disabled:text-ink-2",
  destructive: "disabled:bg-tone-red-ink disabled:text-surface",
  ghostDestructive: "disabled:bg-transparent disabled:text-tone-red-ink",
  onDark: "disabled:bg-white/[0.14] disabled:text-white"
};
const SHARED_CELL = "inline-flex items-center justify-center gap-2 [grid-area:1/1]";
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof BUTTON_VARIANT;
  size?: keyof typeof BUTTON_SIZE;
  hasArrow?: boolean;
  isBusy?: boolean;
  busyLabel?: ReactNode;
}
function BusyFace() {
  return <WilsonFace variant="bare" className="h-3.5 motion-safe:animate-pulse" data-sentry-element="WilsonFace" data-sentry-component="BusyFace" data-sentry-source-file="Button.tsx" />;
}
function ArrowSlot({
  isBusy
}: {
  isBusy: boolean;
}) {
  return <span className="inline-flex w-4 justify-center" data-sentry-component="ArrowSlot" data-sentry-source-file="Button.tsx">
            {isBusy ? <BusyFace /> : <span aria-hidden="true">→</span>}
        </span>;
}
function LabelsInOneCell({
  idle,
  busy,
  isBusy
}: {
  idle: ReactNode;
  busy: ReactNode;
  isBusy: boolean;
}) {
  return <span className="inline-grid" data-sentry-component="LabelsInOneCell" data-sentry-source-file="Button.tsx">
            <span className={cn(SHARED_CELL, isBusy && "invisible")} aria-hidden={isBusy || undefined}>
                {idle}
            </span>
            <span className={cn(SHARED_CELL, !isBusy && "invisible")} aria-hidden={!isBusy || undefined}>
                {busy}
            </span>
        </span>;
}
export function Button({
  variant = "primary",
  size = "md",
  hasArrow = false,
  isBusy = false,
  busyLabel,
  className,
  type,
  children,
  disabled,
  ref,
  ...props
}: ButtonProps & {
  ref?: Ref<HTMLButtonElement>;
}) {
  return <button ref={ref} type={type ?? "button"} className={cn(buttonClassName({
    variant,
    size
  }), className, isBusy && BUSY_LOOKS_IDLE[variant])} disabled={disabled || isBusy} aria-busy={isBusy || undefined} {...props} data-sentry-component="Button" data-sentry-source-file="Button.tsx">
            {busyLabel === undefined ? <>
                    {children}
                    {hasArrow ? <ArrowSlot isBusy={isBusy} /> : isBusy && <BusyFace />}
                </> : <LabelsInOneCell isBusy={isBusy} idle={<>
                            {children}
                            {hasArrow && <ArrowSlot isBusy={false} />}
                        </>} busy={<>
                            {busyLabel}
                            <BusyFace />
                        </>} />}
        </button>;
}